/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: logProfilesApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Centralized API functions for Log Profiles.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';
import { ListLogProfilesResponse, LogProfile, LogProfileFormData } from '../types';

export interface FetchLogProfilesParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
}

export async function fetchLogProfiles(
  params: FetchLogProfilesParams,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<ListLogProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
  });

  const response = await fetch(
    `${buildApiUrl(API_CONFIG.ENDPOINTS.LOG_PROFILES)}?${queryParams}`,
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  return response.json();
}

export async function fetchLogProfileById(
  id: string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<LogProfile> {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.LOG_PROFILES}/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result = await response.json();
  return result.data;
}

export async function createLogProfile(
  data: LogProfileFormData,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<LogProfile> {
  // Ensure permissions are properly formatted
  const payload = {
    name: data.name,
    description: data.description || undefined,
    permissions: data.permissions?.map(p => ({
      moduleSlug: p.moduleSlug,
      logCreate: p.logCreate ?? false,
      logEdit: p.logEdit ?? false,
      logDelete: p.logDelete ?? false,
      notifyCreate: p.notifyCreate ?? false,
      notifyEdit: p.notifyEdit ?? false,
      notifyDelete: p.notifyDelete ?? false,
    })) || [],
  };

  const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOG_PROFILES), {
    method: 'POST',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || errorData.message || 'Failed to create log profile';
    throw new Error(errorMessage);
  }

  const result = await response.json();
  return result.data;
}

export async function updateLogProfile(
  id: string,
  data: Partial<LogProfileFormData>,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<LogProfile> {
  // Ensure permissions are properly formatted if provided
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: any = {};

  if (data.name !== undefined) {
    payload.name = data.name;
  }

  if (data.description !== undefined) {
    payload.description = data.description || undefined;
  }

  if (data.permissions !== undefined) {
    payload.permissions = data.permissions.map(p => ({
      moduleSlug: p.moduleSlug,
      logCreate: p.logCreate ?? false,
      logEdit: p.logEdit ?? false,
      logDelete: p.logDelete ?? false,
      notifyCreate: p.notifyCreate ?? false,
      notifyEdit: p.notifyEdit ?? false,
      notifyDelete: p.notifyDelete ?? false,
    }));
  }

  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.LOG_PROFILES}/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(tenantSlug, userId),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || errorData.message || 'Failed to update log profile';
    throw new Error(errorMessage);
  }

  const result = await response.json();
  return result.data;
}

export async function deleteLogProfile(
  id: string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<void> {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.LOG_PROFILES}/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete log profile');
  }
}

export async function duplicateLogProfile(
  id: string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<LogProfile> {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.LOG_PROFILES}/${id}/duplicate`),
    {
      method: 'POST',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to duplicate log profile');
  }

  const result = await response.json();
  return result.data;
}

export async function fetchLogProfilesQuickList(
  search: string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<LogProfile[]> {
  const queryParams = new URLSearchParams({
    ...(search && { search }),
  });

  const response = await fetch(
    `${buildApiUrl(API_CONFIG.ENDPOINTS.LOG_PROFILES)}/quicklist?${queryParams}`,
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result = await response.json();
  return result.data;
}

